import { inject, Injectable, OnDestroy } from '@angular/core';
import { AddedTreatmentData, selectUserSite, ToothWithDate, UrgencyOption } from '@libs/store';
import { DataState, DISABLE_GLOBAL_ERROR_TOASTS, FfdComponentStore, isNotNull, Loadable, toMonthYearFormat } from '@libs/utils';
import { Store } from '@ngrx/store';
import { ToastService } from '@libs/shared-services';
import {
  PatientDataService,
  ProgressNote,
  ProgressNotesDataService,
  ProgressNoteStatus,
  ProvisionalStatus,
  Treatment,
  TreatmentsDataService,
} from '@ffd/api-client';
import { catchError, filter, switchMap, tap, take } from 'rxjs';
import { concatLatestFrom, tapResponse } from '@ngrx/operators';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { HttpContext } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { RequiredInputType, RequiredInputSurfaceConfiguration } from '@libs/ui-lib';
import { ReviewPlanDialogComponent, ReviewPlanDialogVM } from '../review-plan-dialog';
import { TreatmentCardProviderVM, TreatmentCardVM } from './treatment-card';

export enum TreatmentPlanDialogDate {
  All = 'All',
  Recent = 'Recent',
  Last6Months = 'Last 6 months',
  Last12Months = 'Last 12 months',
}

export interface TreatmentPlanDialogState {
  siteId: string;
  patientId: string;
  progressNotes: Loadable<ProgressNote[]>;
  treatments: Loadable<Treatment[]>;
  treatmentList: TreatmentCardVM[];
  highlightedTeeth: ToothWithDate[];
  providers: TreatmentCardProviderVM[];
  searchQuery: string;
  dateOption: TreatmentPlanDialogDate;
}

export interface TreatmentPlanDialogVM {
  searchQuery: string;
  dateOption: TreatmentPlanDialogDate;
  highlightedTeeth: ToothWithDate[];
  treatmentList: TreatmentCardVM[];
  isLoading: boolean;
  isFetched: boolean;
  providers: TreatmentCardProviderVM[];
  treatmentsCostSum: number;
  numberOfSelectedTreatments: number;
}

interface TreatmentPlanAddedTreatment extends AddedTreatmentData {
  date?: number;
  toothPosition: number[];
  provisionalStatus: ProvisionalStatus;
}

@Injectable()
export class TreatmentPlanDialogService extends FfdComponentStore<TreatmentPlanDialogState, TreatmentPlanDialogVM> implements OnDestroy {
  private readonly store = inject(Store);
  private readonly progressNotesDataService = inject(ProgressNotesDataService);
  private readonly treatmentDataService = inject(TreatmentsDataService);
  private readonly patientDataService = inject(PatientDataService);
  private readonly dialog = inject(Dialog);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private dialogRef: DialogRef<boolean, ReviewPlanDialogComponent> | null = null;

  constructor() {
    super({
      dateOption: TreatmentPlanDialogDate.All,
      siteId: '',
      patientId: '',
      searchQuery: '',
      progressNotes: { state: DataState.Initial, data: [] },
      treatments: { state: DataState.Initial, data: [] },
      highlightedTeeth: [],
      treatmentList: [],
      providers: [],
    });
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.dialogRef?.close();
  }

  public override init(): void {
    this.getProviders();
    // Load progress notes first, then treatments will automatically load
    this.loadProgressNotes();
    this.getTreatments();
  }

  protected override mapStateToViewModel(state: TreatmentPlanDialogState): TreatmentPlanDialogVM {
    return {
      searchQuery: state.searchQuery,
      dateOption: state.dateOption,
      isLoading: state.treatments.state === DataState.Loading,
      isFetched: state.treatments.state === DataState.Fetched,
      highlightedTeeth: state.highlightedTeeth,
      providers: state.providers,
      treatmentList: state.treatmentList
        .filter((treatment) => {
          const lowerCaseSearchQuery = state.searchQuery.toLowerCase();
          return (
            treatment.dentalCode.toLowerCase().includes(lowerCaseSearchQuery) ||
            treatment.treatmentName.toLowerCase().includes(lowerCaseSearchQuery)
          );
        })
        .filter((treatment) => this.filterByDateOption(treatment.date, state.dateOption))
        .map((treatment) => ({
          ...treatment,
          tagsToShow: [
            (treatment?.toothPosition?.length ?? 0) > 1
              ? `${treatment?.toothPosition?.[0]} - ${treatment.toothPosition?.at(-1)}`
              : `${treatment?.toothPosition?.[0] ?? ''}`,
            treatment.surfaces,
          ].filter(Boolean) as string[],
          formattedDate: toMonthYearFormat(new Date(treatment.date ?? 0)),
        })),
      treatmentsCostSum: state.treatmentList.reduce((acc, treatment) => acc + treatment.cost, 0),
      numberOfSelectedTreatments: state.treatmentList.filter((t) => t.selected).length,
    };
  }

  public setSiteId = this.updater<string>((state, siteId): TreatmentPlanDialogState => ({ ...state, siteId }));

  public setPatientId = this.updater<string>((state, patientId): TreatmentPlanDialogState => ({ ...state, patientId }));

  public updateSearchQuery = this.updater<string>((state, searchQuery): TreatmentPlanDialogState => ({ ...state, searchQuery }));

  public updateDateOption = this.updater<TreatmentPlanDialogDate>(
    (state, dateOption): TreatmentPlanDialogState => ({ ...state, dateOption }),
  );

  public updateTreatmentProvider = this.updater<{ index: number; providerId: string }>(
    (state, { index, providerId }): TreatmentPlanDialogState => ({
      ...state,
      treatmentList: state.treatmentList.map((t, i) => (i === index ? { ...t, selectedProviderId: providerId } : t)),
    }),
  );

  public toggleTreatmentSelection = this.updater<number>(
    (state, index): TreatmentPlanDialogState => ({
      ...state,
      treatmentList: state.treatmentList.map((t, i) => (i === index ? { ...t, selected: !t.selected } : t)),
    }),
  );

  public openReviewDialog = this.effect<DialogRef>(($) =>
    $.pipe(
      concatLatestFrom(() => [this.select((state) => state.siteId), this.select((state) => state.patientId)]),
      filter((args): args is [DialogRef, string, string] => !!args[1] && !!args[2]),
      switchMap(([dialogRef, siteId, patientId]) =>
        this.patientDataService
          .getPatientBySite(siteId, patientId, undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            switchMap((patient) => {
              const patientName = `${patient.firstName} ${patient.lastName}`;
              const selectedTreatments = this.state().treatmentList.filter((t) => t.selected);

              this.dialogRef = this.dialog.open<boolean, ReviewPlanDialogVM, ReviewPlanDialogComponent>(ReviewPlanDialogComponent, {
                backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
                disableClose: true,
                data: {
                  patientName,
                  treatmentConsents: selectedTreatments.map((t) => {
                    const treatment = this.state().treatments.data!.find((tr) => tr.ffdDentalCode === t.dentalCode);

                    return {
                      dentalCode: treatment?.ffdDentalCode || '',
                      treatmentName: treatment?.ffdShortDescription || '',
                      consentUrl: treatment?.ffdConsentUrl || '',
                      description: treatment?.ffdFriendlyConsentDescription || '',
                    };
                  }),
                  treatments: selectedTreatments.map((t) => ({
                    dentalCode: t.dentalCode,
                    treatmentName: t.treatmentName,
                    treatmentDescription: t.treatmentDescription || '',
                    cost: t.cost,
                    providerName: this.state().providers.find((p) => p.value === t.selectedProviderId)?.label || '',
                    toothNumber: t.toothPosition,
                    surface: t.surfaces,
                  })),
                } satisfies ReviewPlanDialogVM,
              });

              return this.dialogRef.closed;
            }),
            tap((data) => {
              if (data) {
                dialogRef.close();
              }
            }),
            catchError(() => {
              this.toastService.error(this.translateService.instant('error_message_server_error_patient_by_site'));
              return [];
            }),
          ),
      ),
    ),
  );

  private filterByDateOption(date: number | undefined, dateOption: TreatmentPlanDialogDate): boolean {
    if (TreatmentPlanDialogDate.All === dateOption) {
      return true;
    }

    if (!date) {
      return false;
    }

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const sixMonthsAgo = new Date();
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    const twelveMonthsAgo = new Date();
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);

    switch (dateOption) {
      case TreatmentPlanDialogDate.Recent: {
        return date > startOfToday.getTime();
      }
      case TreatmentPlanDialogDate.Last6Months: {
        return date > sixMonthsAgo.getTime();
      }
      case TreatmentPlanDialogDate.Last12Months: {
        return date > twelveMonthsAgo.getTime();
      }
    }
  }

  private getProviders(): void {
    const user = this.store.selectSignal(selectUserSite)();
    const loggedInUserProviderData = user?.provider
      ? {
          label: `${user.provider.firstName} ${user.provider.lastName}`,
          value: user.provider.id,
        }
      : null;
    const providers: TreatmentCardProviderVM[] = [
      loggedInUserProviderData,
      {
        value: '1',
        label: 'Dr. Anna V. Lucas (Endodontist)',
      },
      {
        value: '2',
        label: 'Dr. Rick Clark, DMD (Periodontist)',
      },
      {
        value: '3',
        label: 'Dr. Michael P. Taylor, DDS (Oral Surgeon)',
      },
      {
        value: '4',
        label: 'C.K. Dental Laboratories',
      },
    ].filter((element): element is TreatmentCardProviderVM => isNotNull(element));

    this.patchState({ providers });
  }

  private readonly loadProgressNotes = this.effect<void>(($) => {
    return $.pipe(
      tap(() => this.patchState({ progressNotes: { state: DataState.Loading, data: [] } })),
      concatLatestFrom(() => [this.select((state) => state.siteId), this.select((state) => state.patientId)]),
      filter((args): args is [void, string, string] => !!args[1] && !!args[2]),
      switchMap(([, siteId, patientId]) =>
        this.progressNotesDataService.getProgressNotesBySite(siteId, patientId).pipe(
          tapResponse(
            (progressNotes) => this.patchState({ progressNotes: { state: DataState.Fetched, data: progressNotes } }),
            (error) => {
              console.error('Error loading progress notes', error);
              this.patchState({ progressNotes: { state: DataState.Error, error: 'Error loading progress notes' } });
            },
          ),
        ),
      ),
    );
  });

  private updateStateBasedOnTreatments(treatments: Treatment[]): void {
    this.patchState({ treatments: { state: DataState.Fetched, data: treatments } });

    //const teethArray = this.store.selectSignal(selectTeeth)()?.teethWithLayers || [];

    //this.patchState({ highlightedTeeth: teethArray.filter((t) => t.treatments.some((t) => t.status === ProgressNoteStatus.P)) });

    const progressNotes = this.state().progressNotes.data || [];

    const proposedTreatmentList: TreatmentPlanAddedTreatment[] = progressNotes
      .map(
        (note) =>
          ({
            id: note.id,
            treatmentName: note.dentalCode.shortDescription || '',
            assetPath: '',
            status: note.status,
            dentalCode: note.dentalCode.code,
            requiredInputData:
              note.toothSurfaces.length > 0
                ? ({
                    inputType: RequiredInputType.Surface,
                    surfaces: note.toothSurfaces,
                    position: note.positions[0],
                  } as RequiredInputSurfaceConfiguration)
                : undefined,
            date: new Date(note.date).getTime(),
            urgency: UrgencyOption.Medium,
            toothPosition: note.positions,
            provisionalStatus: note.provisionalStatus,
          }) satisfies TreatmentPlanAddedTreatment,
      )
      .filter((el) => el.status === ProgressNoteStatus.P);

    this.patchState({
      treatmentList: proposedTreatmentList.map((el) => {
        const treatment = treatments.find((t) => t.ffdDentalCode === el.dentalCode);

        return {
          dentalCode: el.dentalCode,
          treatmentName: treatment?.ffdShortDescription || '',
          treatmentDescription: treatment?.ffdFriendlyDescription || treatment?.ffdLongDescription || '',
          urgency: el.urgency,
          cost: treatment?.ffdEstimatedFee ?? 0,
          selected: true,
          toothPosition: el.toothPosition,
          surfaces: el.requiredInputData ? (el.requiredInputData as RequiredInputSurfaceConfiguration).surfaces.join('') : undefined,
          selectedProviderId: this.state().providers[0].value,
          date: el.date,
          abbreviation: treatment?.ffdAbbrevDescription ?? '',
          provisionalStatus: el.provisionalStatus,
        };
      }),
    });
  }

  private readonly getTreatments = this.effect<void>(($) => {
    return $.pipe(
      tap(() => this.patchState({ treatments: { state: DataState.Loading, data: [] } })),
      // Wait for local progress notes to be loaded first
      switchMap(() =>
        this.select((state) => state.progressNotes.state).pipe(
          filter((state) => state === DataState.Fetched),
          take(1),
          switchMap(() =>
            this.treatmentDataService.getTreatments().pipe(
              tapResponse(
                (treatments) => this.updateStateBasedOnTreatments(treatments),
                (error) => {
                  console.error('Error fetching treatments', error);
                  this.patchState({ treatments: { state: DataState.Error, error: 'Error fetching treatments' } });
                },
              ),
            ),
          ),
        ),
      ),
    );
  });

  public onDateOptionChange(option: TreatmentPlanDialogDate | TreatmentPlanDialogDate[] | null): void {
    if (!isNotNull(option)) {
      return;
    }

    if (Array.isArray(option)) {
      if (option.length > 0) {
        this.updateDateOption(option[0]);
      }

      return;
    }

    this.updateDateOption(option);
  }
}
